import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

export interface Notification {
  id: string
  user_id: string
  type:
    | 'phase_completed'
    | 'project_submitted'
    | 'file_uploaded'
    | 'comment_added'
    | 'comment_resolved'
    | 'project_status_changed'
    | 'feedback_round_created'
    | 'feedback_round_completed'
    | 'project_completed'
    | 'feedback_batch_summary'
    // Phase 5 notification types
    | 'maintenance_item_created'
    | 'maintenance_item_completed'
    | 'maintenance_priority_alert'
    | 'maintenance_cycle_started'
    | 'maintenance_cycle_closed'
    // Phase 5 extended notification types
    | 'hour_package_low'
    | 'time_entry_added'
    | 'suggestion_proposed'
    | 'suggestion_accepted'
    | 'suggestion_rejected'
    | 'domain_expiry_warning'
    | 'monthly_report_ready'
    // Phase 2 content feedback notification types
    | 'content_feedback_added'
    | 'content_feedback_resolved'
    | 'content_approved'
    // Build notification types
    | 'build_completed'
    | 'build_failed'
  title: string
  message: string
  data: Record<string, any>
  link: string | null
  read: boolean
  created_at: string
}

export interface NotificationPreferences {
  user_id: string
  email_phase_complete: boolean
  email_project_submitted: boolean
  email_file_uploads: boolean
  email_feedback_summary: boolean
  email_daily_digest: boolean
  digest_frequency: 'daily' | 'weekly' | 'never'
  digest_time: string
  in_app_notifications: boolean
  created_at: string
  updated_at: string
}

export const useNotifications = () => {
  const { user } = useAuth()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null)

  // Fetch notifications
  const fetchNotifications = async (limit = 50) => {
    if (!user) return

    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) {
        console.error('Error fetching notifications:', error)
        throw error
      }

      setNotifications(data || [])
    } catch (error) {
      console.error('Error fetching notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  // Fetch unread count
  const fetchUnreadCount = async () => {
    if (!user) return

    try {
      const { data, error } = await supabase
        .rpc('get_unread_notification_count', { p_user_id: user.id })

      if (error) {
        console.error('RPC error fetching unread count:', error)
        // Fallback to manual count if RPC fails
        const { count, error: countError } = await supabase
          .from('notifications')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('read', false)

        if (!countError) {
          setUnreadCount(count || 0)
        }
        return
      }

      setUnreadCount(data || 0)
    } catch (error) {
      console.error('Error fetching unread count:', error)
    }
  }

  // Fetch notification preferences
  const fetchPreferences = async () => {
    if (!user) return

    try {
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single()

      if (error && error.code !== 'PGRST116') {
        // PGRST116 = no rows returned
        throw error
      }

      setPreferences(data)
    } catch (error) {
      console.error('Error fetching preferences:', error)
    }
  }

  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    if (!user) return

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId)
        .eq('user_id', user.id)

      if (error) throw error

      // Update local state
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      )
      setUnreadCount((prev) => Math.max(0, prev - 1))
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!user) return

    try {
      const { error } = await supabase.rpc('mark_all_notifications_read', {
        p_user_id: user.id,
      })

      if (error) throw error

      // Update local state
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
      setUnreadCount(0)
    } catch (error) {
      console.error('Error marking all as read:', error)
    }
  }

  // Delete notification
  const deleteNotification = async (notificationId: string) => {
    if (!user) return

    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId)
        .eq('user_id', user.id)

      if (error) throw error

      // Update local state
      const deletedNotification = notifications.find((n) => n.id === notificationId)
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId))
      if (deletedNotification && !deletedNotification.read) {
        setUnreadCount((prev) => Math.max(0, prev - 1))
      }
    } catch (error) {
      console.error('Error deleting notification:', error)
    }
  }

  // Update notification preferences
  const updatePreferences = async (
    updates: Partial<Omit<NotificationPreferences, 'user_id' | 'created_at' | 'updated_at'>>
  ) => {
    if (!user) return

    try {
      const { data, error } = await supabase
        .from('notification_preferences')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id)
        .select()
        .single()

      if (error) throw error

      setPreferences(data)
      return { success: true }
    } catch (error) {
      console.error('Error updating preferences:', error)
      return { success: false, error }
    }
  }

  // Subscribe to realtime notifications
  useEffect(() => {
    if (!user) return

    fetchNotifications()
    fetchUnreadCount()
    fetchPreferences()

    // Set up realtime subscription
    const channel = supabase
      .channel(`notifications:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          // Add new notification to the list
          const newNotification = payload.new as Notification
          setNotifications((prev) => [newNotification, ...prev])
          setUnreadCount((prev) => prev + 1)
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          // Update notification in the list
          const updatedNotification = payload.new as Notification
          setNotifications((prev) =>
            prev.map((n) => (n.id === updatedNotification.id ? updatedNotification : n))
          )
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          // Remove notification from the list
          const deletedNotification = payload.old as Notification
          setNotifications((prev) => prev.filter((n) => n.id !== deletedNotification.id))
        }
      )
      .subscribe()

    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(channel)
    }
  }, [user])

  return {
    notifications,
    unreadCount,
    loading,
    preferences,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    updatePreferences,
    refetch: fetchNotifications,
  }
}
